/**
 * Enhanced Calendar Component for CheckoutPage
 * Shows availability with visual indicators for booked/available dates
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { fetchProviderAvailability, dateToPhoenixISO, phoenixTodayISO } from '../../util/availabilityUtils';
import css from './EnhancedCalendar.module.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EnhancedCalendar = ({ 
  providerId, 
  selectedDate, 
  onDateSelect, 
  onAvailabilityUpdate,
  className,
  minDate,
  availabilityPlan, // Add this prop to receive the listing's availability plan
  publicData,
  listingId, // Optional: pass listingId to backend to fetch plan via public SDK if needed
  // When true, explicitly allow consecutive bookable days: if a client plan exists,
  // ignore API disabledDates for weekday gating and only block by plan + bookings.
  // This helps ensure Mon–Thu style plans show Monday through Thursday without gaps.
  allowConsecutiveBookableDays = true
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [disabledDates, setDisabledDates] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [planFromApi, setPlanFromApi] = useState(null);

  // (debug logging removed)

  // Resolve effective plan for gating: ONLY use the listing's plan if it has entries.
  // Do NOT use API-inferred plan for gating (can be incomplete), but we still store it for debugging.
  const effectivePlan = React.useMemo(() => {
    // Prefer publicData.availabilityPlanEntries if present
    const hasEntries = p => p && Array.isArray(p.entries) && p.entries.length > 0;
    if (publicData && Array.isArray(publicData.availabilityPlanEntries) && publicData.availabilityPlanEntries.length > 0) {
      return { type: 'availability-plan/time', entries: publicData.availabilityPlanEntries, timezone: availabilityPlan?.timezone };
    }
    if (hasEntries(availabilityPlan)) return availabilityPlan;
    return null; // no client-side plan; we'll fall back to API disabledDates
  }, [availabilityPlan, publicData]);
  const allowedDayIndexSet = React.useMemo(() => {
    if (!effectivePlan || !Array.isArray(effectivePlan.entries)) return null;
    const dayIndexToStr = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const normalize = d => {
      // Accept multiple input shapes for dayOfWeek
      if (typeof d === 'number') {
        // Support both 0..6 (Sun..Sat) and 1..7 (Mon..Sun)
        if (d >= 0 && d <= 6) return d;
        if (d >= 1 && d <= 7) return d === 7 ? 0 : d; // 1->Mon(1), ..., 6->Sat(6), 7->Sun(0)
        return -1;
      }
      if (typeof d === 'string') {
        const lower = d.toLowerCase();
        // Support full names and 3-letter abbreviations
        const short = lower.slice(0, 3);
        return dayIndexToStr.indexOf(short);
      }
      return -1;
    };
    const indices = new Set();
    effectivePlan.entries.forEach(e => {
      const idx = normalize(e?.dayOfWeek);
      // Treat undefined seats as 1 (available) to avoid dropping valid days
      const seatsVal = e?.seats == null ? 1 : Number(e.seats);
      if (idx >= 0 && seatsVal > 0) indices.add(idx);
    });
  // (debug logging removed)
    return indices;
  }, [effectivePlan]);

  // (debug logging removed)

  // Load availability for the current provider
  useEffect(() => {
    if (!providerId) return;

  // (debug logging removed)

  const loadAvailability = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await fetchProviderAvailability(providerId, listingId, availabilityPlan);
        const disabled = data?.disabledDates || [];
        const booked = data?.bookedDates || [];

        setDisabledDates(disabled);
        setBookedDates(booked);
        // Capture availability plan from API as a fallback if listing prop is missing
        if (data?.availabilityPlan) {
          setPlanFromApi(data.availabilityPlan);
        }
        
        // Check if we're using fallback mode
        if (data?.fallback) {
          setIsUsingFallback(true);
          setError(null); // Don't show error for fallback mode
        } else {
          setIsUsingFallback(false);
          setError(null);
        }
        
        // Notify parent component of availability data
        if (onAvailabilityUpdate) {
          onAvailabilityUpdate(disabled);
        }
      } catch (err) {
        // keep minimal error visibility if needed; otherwise silent
        setError(err);
        setIsUsingFallback(true);
        setDisabledDates([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAvailability();
  }, [providerId, listingId, availabilityPlan]); // Add availabilityPlan as dependency
  // Get today's date in Phoenix timezone (YYYY-MM-DD)
  const getTodayString = () => phoenixTodayISO();

  // Compute Phoenix weekday index (0=Sun..6=Sat) from a Phoenix ISO date string
  const phoenixWeekdayIndex = (phoenixIsoDateString) => {
    try {
      // Construct a Date at Phoenix midnight (fixed -07:00; Phoenix has no DST)
      const d = new Date(`${phoenixIsoDateString}T00:00:00-07:00`);
      const short = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Phoenix', weekday: 'short' }).format(d);
      const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return map[short] ?? d.getUTCDay();
    } catch {
      return new Date(`${phoenixIsoDateString}T00:00:00-07:00`).getUTCDay();
    }
  };

  const isAllowedByPlan = (phoenixIsoDateString) => {
    if (!allowedDayIndexSet || allowedDayIndexSet.size === 0) return true;
    const dayIdx = phoenixWeekdayIndex(phoenixIsoDateString);
    return allowedDayIndexSet.has(dayIdx);
  };

  const isDateDisabled = (dateString) => {
    const today = getTodayString();
    const min = minDate || today;
    
    // Past dates or before minDate
    if (dateString < min) {
      return true;
    }
    
    // If we have a concrete plan, don't let API disabledDates (which may include inferred plan blocks)
    // hide allowed weekdays. Rely on plan gating + bookings only.
    if (allowedDayIndexSet) {
  if (!isAllowedByPlan(dateString)) {
        return true;
      }
  if (isDateBooked(dateString)) {
        return true;
      }
      // If explicit consecutive bookable days are NOT enabled, allow API disabledDates
      // to further restrict availability even when plan allows the weekday.
  if (!allowConsecutiveBookableDays && disabledDates.includes(dateString)) {
        return true;
      }
      return false;
    }

    // No client plan available: fall back to API disabledDates (plan restrictions + bookings)
    if (disabledDates.includes(dateString)) {
      return true;
    }
    return false;
  };

  // Check if a date is booked (separate from availability plan restrictions)
  const isDateBooked = (dateString) => bookedDates.includes(dateString);

  // Check if date is unavailable due to availability plan
  const isDateUnavailableByPlan = (dateString) => {
    const today = getTodayString();
    const isFuture = dateString >= today;
    let result = false;
    if (allowedDayIndexSet) {
      // Primary: client plan gating
      const planBlocks = isFuture && !isAllowedByPlan(dateString);
      if (allowConsecutiveBookableDays) {
        return planBlocks;
      }
      // Without consecutive enforcement, also honor API disabledDates as unavailable
      result = planBlocks || (isFuture && disabledDates.includes(dateString));
      return result;
    }
    // Fallback: infer from API disabled list
    result = isFuture && disabledDates.includes(dateString);
    return result;
  };

  // (debug logging removed)

  // Get calendar days for the current month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
  // (debug logging removed)
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Starting day of week (0 = Sunday)
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      // Use Phoenix timezone ISO (YYYY-MM-DD) to match server values
      const dateString = dateToPhoenixISO(date);
      
      const dayInfo = {
        date,
        dateString,
        day,
  isToday: dateString === getTodayString(),
        isSelected: dateString === selectedDate,
        isDisabled: isDateDisabled(dateString),
        isBooked: isDateBooked(dateString),
        isUnavailableByPlan: isDateUnavailableByPlan(dateString)
      };
      
      days.push(dayInfo);
      
  // (debug logging removed)
    }
    
    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateClick = (dayInfo) => {
    if (dayInfo.isDisabled || dayInfo.isBooked || dayInfo.isUnavailableByPlan) return;
    onDateSelect(dayInfo.dateString);
  };

  const calendarDays = getCalendarDays();
  const monthYear = `${MONTHS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;

  return (
    <div className={classNames(css.calendar, className)}>
      {/* Calendar Header */}
      <div className={css.calendarHeader}>
        <button 
          type="button"
          className={css.navButton}
          onClick={goToPreviousMonth}
          aria-label="Previous month"
        >
          ‹
        </button>
        
        <h3 className={css.monthYear}>{monthYear}</h3>
        
        <button 
          type="button"
          className={css.navButton}
          onClick={goToNextMonth}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

  {/* Debug overlay removed */}
      {/* Days of week header */}
      <div className={css.daysHeader}>
        {DAYS.map(day => (
          <div key={day} className={css.dayHeader}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className={css.calendarGrid}>
        {calendarDays.map((dayInfo, index) => (
          <div key={index} className={css.dayCell}>
            {dayInfo && (
              <button
                type="button"
                className={classNames(css.dayButton, {
                  [css.today]: dayInfo.isToday,
                  [css.selected]: dayInfo.isSelected,
                  [css.disabled]: dayInfo.isDisabled && !dayInfo.isUnavailableByPlan && !dayInfo.isBooked, // Only past dates
                  [css.booked]: dayInfo.isBooked, // Actually booked dates (red)
                  [css.unavailable]: dayInfo.isUnavailableByPlan, // Not allowed by availability plan (red)
                  [css.available]: !dayInfo.isDisabled && !dayInfo.isBooked && !dayInfo.isUnavailableByPlan
                })}
                onClick={() => handleDateClick(dayInfo)}
                disabled={dayInfo.isDisabled || dayInfo.isBooked || dayInfo.isUnavailableByPlan || isLoading}
                title={
                  dayInfo.isBooked ? 'Date is booked' :
                  dayInfo.isUnavailableByPlan ? 'Not available (provider schedule)' :
                  dayInfo.isDisabled ? 'Date not available' :
                  'Available date'
                }
              >
                {dayInfo.day}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className={css.loadingOverlay}>
          <div className={css.loadingText}>Loading availability...</div>
        </div>
      )}

      {/* Error state - only show if not using fallback */}
      {error && !isUsingFallback && (
        <div className={css.errorMessage}>
          Unable to load availability. Please select a date manually.
        </div>
      )}

  {/* Fallback mode notice removed per request */}

  {/* Availability status hidden to keep UI minimal */}

      {/* Legend */}
      <div className={css.legend}>
        <div className={css.legendItem}>
          <span className={classNames(css.legendIcon, css.available)}>●</span>
          Available
        </div>
        <div className={css.legendItem}>
          <span className={classNames(css.legendIcon, css.booked)}>●</span>
          Booked
        </div>
        <div className={css.legendItem}>
          <span className={classNames(css.legendIcon, css.disabled)}>●</span>
          Past dates
        </div>
      </div>
    </div>
  );
};

EnhancedCalendar.propTypes = {
  providerId: PropTypes.string.isRequired,
  selectedDate: PropTypes.string,
  onDateSelect: PropTypes.func.isRequired,
  onAvailabilityUpdate: PropTypes.func,
  className: PropTypes.string,
  minDate: PropTypes.string,
  availabilityPlan: PropTypes.object, // Add this prop type
  publicData: PropTypes.object,
  listingId: PropTypes.string,
  allowConsecutiveBookableDays: PropTypes.bool
};

export default EnhancedCalendar;
